import { NetworkStatus, useQuery } from "@apollo/client";
import type {
  IFinancialsTemplatesQuery,
  IFinancialsTemplatesQueryVariables,
} from "components/financials/modern/graphql/__generated__/FinancialsTemplatesQuery.generated";
import FinancialsTemplatesQuery from "components/financials/modern/graphql/FinancialsTemplatesQuery";
import UserFinancialsTemplatesQuery from "components/financials/modern/graphql/UserFinancialsTemplatesQuery";
import type {
  IUserFinancialsTemplatesQuery,
  IUserFinancialsTemplatesQueryVariables,
} from "components/financials/modern/graphql/__generated__/UserFinancialsTemplatesQuery.generated";
import type {
  IStarredTemplatesQuery,
  IStarredTemplatesQueryVariables,
} from "components/financials/modern/graphql/__generated__/StarredTemplatesQuery.generated";
import { StarredTemplatesQuery } from "components/financials/modern/graphql/StarredTemplatesQuery";
import type { DeepPartial } from "utility-types";

export function useStarredTemplates(initialVariables?: DeepPartial<IFinancialsTemplatesQueryVariables>) {
  const { data, loading, refetch } = useQuery<IStarredTemplatesQuery, IStarredTemplatesQueryVariables>(
    StarredTemplatesQuery,
    {
      variables: initialVariables as IFinancialsTemplatesQueryVariables,
    },
  );
  return {
    templates: data?.selfie?.starredTemplates.edges.map((edge) => edge.node) || [],
    loading,
    refetch,
    totalCount: data?.selfie?.starredTemplates.totalCount,
  };
}

export function useOwnTemplates(initialVariables?: DeepPartial<IFinancialsTemplatesQueryVariables>) {
  const { data, loading, refetch } = useQuery<IUserFinancialsTemplatesQuery, IUserFinancialsTemplatesQueryVariables>(
    UserFinancialsTemplatesQuery,
    {
      variables: initialVariables as IFinancialsTemplatesQueryVariables,
    },
  );

  return {
    templates: data?.financialsTemplates?.nodes || [],
    breakdown: data?.financialsTemplates?.breakdown,
    loading,
    refetch,
  };
}

export function usePublicTemplates(initialVariables: DeepPartial<IFinancialsTemplatesQueryVariables>) {
  const {
    data,
    fetchMore,
    loading,
    refetch,
    variables: queryVariables,
    networkStatus,
  } = useQuery<IFinancialsTemplatesQuery, IFinancialsTemplatesQueryVariables>(FinancialsTemplatesQuery, {
    notifyOnNetworkStatusChange: true,
    variables: initialVariables as IFinancialsTemplatesQueryVariables,
  });

  return {
    templates: data?.financialsTemplates?.nodes || [],
    loading: loading && networkStatus !== NetworkStatus.fetchMore,
    refetch,
    fetchMore,
    variables: queryVariables,
    networkStatus,
    pageInfo: data?.financialsTemplates?.pageInfo,
    totalCount: data?.financialsTemplates.totalCount,
  };
}
